import styled from '@emotion/styled';
import type { ColumnDirection } from '@hotjar/hotsauce';
import {
  Loader,
  Button,
  Icon,
  IconButton,
  Link,
  Stack,
  Table,
  tokens,
  Tooltip,
} from '@hotjar/hotsauce';
import { epochDateTime, epochDateShort } from '@hotjar/comments/lib/time';
import { stripDomain } from '@hotjar/webapp/utils/urls';
import { sanitizeUUID } from '@hotjar/webapp/lib/sanitize';
import type { ReactNode } from 'react';
import { useCallback, useMemo } from 'react';
import { ResponseFeedbackTooltip } from '@hotjar/webapp/features/Surveys/Responses/ResponseFeedbackTooltip';
import { OSDetails } from '@hotjar/webapp/components/Details/OSDetails';
import { BrowserDetails } from '@hotjar/webapp/components/Details/BrowserDetails';
import { DeviceDetails } from '@hotjar/webapp/components/Details/DeviceDetails';
import { truncateUrl } from '@hotjar/webapp/features/Feedback/utils/truncateUrl';
import { getLocalizedCountryNameFromCode } from '@hotjar/country-lib';
import type { CountryCode } from '@hotjar/country-lib';
import { usePermission } from '@hotjar/webapp/domains/authorization';
import { useCurrentSite } from '@hotjar/site';
import { useNavigation } from '@hotjar/navigation';
import type { SurveyColumnOptions } from '@hotjar/webapp/../../../feedback/domain/Surveys/Responses/SurveyResponsesColumns';
import { useTranslation } from '@hotjar/translations';
import { QuestionTypes, truncateUuid } from '../../../../../domain/Surveys/Questions';
import type { SurveyQuestion } from '../../../../../domain/Surveys/Questions';
import type { SurveyResponseDto as Response } from '../../../../../domain/Surveys/Responses/SurveyResponseDto';
import { useSelectedQuestion } from '../../SurveyResultsView/useSelectedQuestion';
import { Answers } from './Answers';
import { SurveyRecording } from './SurveyRecording';
import { useNavigateToSurveyResponse } from '../../../../../hooks/useNavigateToSurveyResponse';

const NoWrap = styled.span`
  display: inline-block;
  white-space: nowrap;
`;

const CenteredStack = styled(Stack)`
  align-items: center;
`;

const PageDetails = styled(Link)`
  text-overflow: ellipsis;
  white-space: nowrap;
  display: inline-block;
  overflow: hidden;
`;

const Flag = styled.div`
  flex-shrink: 0;
`;

const AnonymousUser = styled.span`
  color: ${tokens.color.text_disabled};
`;

const UserIdContainer = styled.span`
  color: ${tokens.color.text_light};
`;

const Container = styled.div`
  display: flex;
  flex-direction: column;
`;

const StyledIcon = styled(Icon)`
  color: ${tokens.color.secondary_cta};
`;

function UserId({ userId }: { userId: string }) {
  const sanitizedUserId = sanitizeUUID(userId);
  const { t } = useTranslation('feedback', { keyPrefix: 'surveys.responses-list' });
  return (
    <Tooltip text={t('hotjar-user-id')}>
      {!sanitizedUserId ? (
        <AnonymousUser>{t('anonymous')}</AnonymousUser>
      ) : (
        <UserIdContainer>{sanitizedUserId}</UserIdContainer>
      )}
    </Tooltip>
  );
}

export interface SortState {
  key: string;
  direction: ColumnDirection;
}

export interface ResponsesListProps {
  surveyId: number;
  questions?: SurveyQuestion[];
  responses: Response[] | undefined;
  sortState: SortState;
  onDeleteResponseClicked: (response: Response) => void;
  onSortChanged: (sortState: SortState) => void;
  columnOptions: SurveyColumnOptions;
  coveragePercentage: number;
  isExternalLinkSurvey: boolean;
}

export function ResponsesList(props: ResponsesListProps): JSX.Element | null {
  const {
    surveyId,
    questions = [],
    responses,
    sortState,
    columnOptions,
    onSortChanged,
    onDeleteResponseClicked,
    coveragePercentage,
    isExternalLinkSurvey,
  } = props;

  const { t } = useTranslation('feedback', { keyPrefix: 'surveys.responses-list' });
  const { t: tHeader } = useTranslation('feedback', { keyPrefix: 'survey-response.table.header' });

  const selectedQuestion = useSelectedQuestion(surveyId, true);
  const { data: site } = useCurrentSite();

  const selectResponse = useNavigateToSurveyResponse(surveyId);

  const isVisibleQuestion = useCallback(
    (question: SurveyQuestion) => {
      return (
        (selectedQuestion === undefined ||
          selectedQuestion === 'ALL' ||
          selectedQuestion.uuid === question.uuid) &&
        (question.type !== QuestionTypes.TITLE_AND_DESCRIPTION || columnOptions.statements.show)
      );
    },
    [columnOptions, selectedQuestion],
  );

  const visibleQuestions = useMemo(
    () => questions.filter(isVisibleQuestion),
    [questions, isVisibleQuestion],
  );

  const { topBarHeight: topNavHeight } = useNavigation();

  // FIXME: Move this component outside ResponsesList.
  // eslint-disable-next-line react/no-unstable-nested-components
  const SortHeader = ({
    title,
    label,
    sortKey,
    children,
    ...headerProps
  }: {
    title: string;
    label?: string;
    sortKey: string;
    children?: ReactNode | ReactNode[];
  }) => {
    return (
      <Table.HeadCell
        {...headerProps}
        title={title}
        aria-label={label ?? title}
        isSortable
        sortLabel={t('sort-title-column', { title })}
        sortDirection={sortState.key === sortKey ? sortState.direction : 'none'}
        onSort={(newDirection) => onSortChanged({ key: sortKey, direction: newDirection })}
      >
        {children ?? title}
      </Table.HeadCell>
    );
  };

  const hasSurveyDeletePermission = usePermission('surveys-responses-delete');
  const hasSurveyResponseReadPermission = usePermission('surveys-responses-read');
  const hasSurveyResponseVotePermission = usePermission('surveys-responses-vote');

  if (!responses) return <Loader size="xl" />;

  return (
    <Container>
      {responses.length !== 0 && (
        <Table
          tableName={t('table-name')}
          caption={t('table-caption', { responseCount: responses.length })}
        >
          <Table.Head size="m">
            <Table.HeadRow stickyOffset={topNavHeight}>
              {columnOptions.index.show && (
                <SortHeader title={columnOptions.index.label} sortKey="index" />
              )}
              {visibleQuestions.map((question) => {
                const sortKey = `question:${truncateUuid(question)}`;

                return (
                  <SortHeader
                    key={question.uuid}
                    title={question.text}
                    sortKey={sortKey}
                    label={question.text}
                  >
                    {question.text}
                  </SortHeader>
                );
              })}
              {columnOptions.page.show && (
                <SortHeader title={columnOptions.page.label} sortKey="response_url" />
              )}
              {columnOptions.date.show && (
                <SortHeader title={columnOptions.date.label} sortKey="created_epoch_time" />
              )}
              {columnOptions.device.show && (
                <SortHeader title={columnOptions.device.label} sortKey="device">
                  <Tooltip text={t('column.device')}>
                    <Icon icon="device_desktop" />
                  </Tooltip>
                </SortHeader>
              )}
              {columnOptions.location.show && (
                <SortHeader title={columnOptions.location.label} sortKey="location_id" />
              )}
              {columnOptions.browser.show && (
                <SortHeader title={columnOptions.browser.label} sortKey="browser">
                  <Tooltip text={t('column.browser')}>
                    <Icon icon="site" />
                  </Tooltip>
                </SortHeader>
              )}
              {columnOptions.user.show && (
                <SortHeader title={columnOptions.user.label} sortKey="visitor_uuid" />
              )}
              {columnOptions.os.show && (
                <SortHeader title={columnOptions.os.label} sortKey="os">
                  <Tooltip text={t('column.operating-system')}>
                    <span>{tHeader('os')}</span>
                  </Tooltip>
                </SortHeader>
              )}
              <Table.HeadCell title={t('column.actions')}>{t('column.actions')}</Table.HeadCell>
            </Table.HeadRow>
          </Table.Head>
          <Table.Body>
            {responses.map((response) => {
              return (
                <Table.Row key={response.id}>
                  {columnOptions.index.show && <Table.Cell>{response.index}</Table.Cell>}
                  {visibleQuestions.map((question) => (
                    <Table.Cell key={question.uuid}>
                      <Answers response={response} question={question} />
                    </Table.Cell>
                  ))}
                  {columnOptions.page.show && (
                    <Table.Cell>
                      <Tooltip text={response.response_url}>
                        <span>
                          <PageDetails isExternal kind="inline" href={response.response_url}>
                            {truncateUrl(stripDomain(response.response_url))}
                          </PageDetails>
                        </span>
                      </Tooltip>
                    </Table.Cell>
                  )}
                  {columnOptions.date.show && (
                    <Table.Cell>
                      <Tooltip text={epochDateTime(response.created_epoch_time)}>
                        <NoWrap>{epochDateShort(response.created_epoch_time)}</NoWrap>
                      </Tooltip>
                    </Table.Cell>
                  )}
                  {columnOptions.device.show && (
                    <Table.Cell>
                      <DeviceDetails
                        device={response.device}
                        x={response.window_size[0]}
                        y={response.window_size[1]}
                      />
                    </Table.Cell>
                  )}
                  {columnOptions.location.show && (
                    <Table.Cell>
                      <CenteredStack spacing="x1">
                        <Flag className={`flag flag-${response.country_code ?? ''}`} />
                        {response.country_code !== null
                          ? getLocalizedCountryNameFromCode(
                              response.country_code.toUpperCase() as CountryCode,
                            )
                          : t('unknown-location')}
                      </CenteredStack>
                    </Table.Cell>
                  )}
                  {columnOptions.browser.show && (
                    <Table.Cell>
                      <BrowserDetails browser={response.browser} />
                    </Table.Cell>
                  )}
                  {columnOptions.user.show && (
                    <Table.Cell>
                      <UserId userId={response.visitor_uuid} />
                    </Table.Cell>
                  )}
                  {columnOptions.os.show && (
                    <Table.Cell>
                      <OSDetails os={response.os} />
                    </Table.Cell>
                  )}
                  <Table.Cell>
                    <CenteredStack spacing="x2">
                      {hasSurveyResponseVotePermission && site && responses.length > 3 && (
                        <ResponseFeedbackTooltip
                          votingAction={response.voting_action}
                          siteId={site.id}
                          surveyId={surveyId}
                          responseId={response.id}
                          appendTo={document.body}
                          color={tokens.color.secondary_cta}
                        />
                      )}
                      {hasSurveyResponseReadPermission && (
                        <Button
                          aria-label={t('view-response-index', { index: response.index })}
                          kind="secondary"
                          size="s"
                          onClick={() => selectResponse(response?.id)}
                        >
                          {t('view-response')}
                        </Button>
                      )}

                      {!isExternalLinkSurvey && (
                        <SurveyRecording
                          recordingUrl={response.recording_url}
                          coveragePercentage={coveragePercentage}
                          contentsquareUrl={response.contentsquare_replay_link}
                          siteId={site?.id}
                          type="icon"
                          trackUiPosition="survey_response_list_view_play_icon"
                        />
                      )}
                      {hasSurveyDeletePermission && (
                        <IconButton
                          size="s"
                          icon={<StyledIcon icon="trash" />}
                          description={t('delete-response-index', { index: response.index })}
                          tooltipText={t('delete-response-index', { index: response.index })}
                          onClick={() => onDeleteResponseClicked(response)}
                          kind="ghost"
                        />
                      )}
                    </CenteredStack>
                  </Table.Cell>
                </Table.Row>
              );
            })}
          </Table.Body>
        </Table>
      )}
    </Container>
  );
}
